import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { t, type Key } from '../i18n';
import { haptic } from '../telegram';
import { fmt, money } from './fmt';

type Slice = {
  id: string;
  status: 'offered' | 'accepted' | 'completed';
  scope: 'case' | 'transport';
  case: { number: string };
  route: { from_place: string | null; to_place: string | null; origin_country: string | null; dest_country: string | null } | null;
  dates: { window_from: string | null; window_to: string | null } | null;
  partner_payment: { amount_minor: number; currency: string } | null;
  checklist: { document_type_key: string; name: Record<string, string>; status: string }[] | null;
  family_contacts: { full_name: string; role: string; identities: { kind: string; value: string }[] }[] | null;
  partner_trips: { driver_name: string | null; vehicle_plate: string | null; vehicle_model: string | null }[];
  respond_by: string | null;
  drivers: { user_id: string; full_name: string; plate: string | null; model: string | null }[];
};

const REASONS = ['no_vehicle', 'price', 'no_route', 'other'] as const;

/** Пропозиція для партнера — лише те, що відкрито в передачі (зріз app.handover_detail за visibility). */
export function Offer({ handoverId }: { handoverId: string }) {
  const [d, setD] = useState<Slice | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api<Slice>(`/tg/app/partner/offers/${handoverId}`).then(setD).catch(() => setMsg(t('error')));
  useEffect(() => {
    load();
  }, [handoverId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(path: string, body?: unknown) {
    setBusy(true);
    try {
      const r = await api<{ message?: string }>(`/tg/app/partner/offers/${handoverId}/${path}`, { body: body ?? {} });
      haptic('success');
      setMsg(r.message ?? null);
      setDeclining(false);
      await load();
    } catch (e) {
      haptic('error');
      setMsg(e instanceof ApiError ? String(e.detail ?? e.code) : t('error'));
    } finally {
      setBusy(false);
    }
  }

  if (!d) return <div className="center">{msg ?? t('loading')}</div>;
  const r = d.route;
  const left = d.respond_by ? Math.max(0, Math.round((new Date(d.respond_by).getTime() - Date.now()) / 60000)) : null;
  const trip = d.partner_trips[0];

  return (
    <div className="app">
      <div className="body">
        <span className="pill">{t(`status_${d.status}` as Key)}</span>
        <div>
          <div className="k">{d.case.number} · {t(d.scope === 'case' ? 'scope_case' : 'scope_transport')}</div>
          {r && <h1 className="h1">{r.from_place ?? r.origin_country} → {r.to_place ?? r.dest_country}</h1>}
          {d.partner_payment && (
            <div className="k">{t('your_pay')} {money(d.partner_payment.amount_minor, d.partner_payment.currency)} {t('after_act')}</div>
          )}
          {d.status === 'offered' && left !== null && <div className="k">{t('answer_in')} {left} {t('min')}</div>}
        </div>

        <div className="card">
          <div className="lbl">{t('route')}</div>
          {r ? (
            <>
              <div className="row"><span className="k">{t('pickup')}</span><span>{[r.from_place, r.origin_country].filter(Boolean).join(', ')}</span></div>
              <div className="row"><span className="k">{t('deliver_by')}</span><span>{[r.to_place, r.dest_country].filter(Boolean).join(', ')}</span></div>
            </>
          ) : <div className="lock">{t('hidden')}</div>}
          {d.dates && (
            <div className="row"><span className="k">{fmt(d.dates.window_from)}</span><span>{fmt(d.dates.window_to)}</span></div>
          )}
        </div>

        {d.checklist && (
          <div className="card">
            <div className="lbl">{t('documents')}</div>
            {d.checklist.map((c) => (
              <div className="row" key={c.document_type_key}>
                <span>{(c.name && (c.name as Record<string, string>)[document.documentElement.lang]) || c.name?.uk || c.document_type_key}</span>
                <span className="k">{c.status === 'accepted' ? '✓' : '…'}</span>
              </div>
            ))}
          </div>
        )}

        <div className="card">
          <div className="lbl">{t('family_contacts')}</div>
          {d.family_contacts ? d.family_contacts.map((f, i) => (
            <div key={i}>{f.full_name}<div className="k">{f.identities.map((x) => x.value).join(', ')}</div></div>
          )) : <div className="lock">{t('hidden')}</div>}
        </div>

        {d.status === 'accepted' && (
          <div className="card">
            <div className="lbl">{t('driver')}</div>
            {trip ? (
              <div><b>{trip.driver_name}</b><div className="k">{[trip.vehicle_model, trip.vehicle_plate].filter(Boolean).join(' · ')}</div></div>
            ) : d.drivers.map((x) => (
              <button key={x.user_id} type="button" className="opt" disabled={busy} onClick={() => act('driver', { user_id: x.user_id })}>
                <span style={{ flex: 1 }}><b>{x.full_name}</b><br /><span className="k">{[x.model, x.plate].filter(Boolean).join(' · ')}</span></span>
                <span>{t('assign')}</span>
              </button>
            ))}
          </div>
        )}

        {declining && (
          <div className="card">
            <div className="lbl">{t('why')}</div>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} aria-label={t('why_other')} />
            <div className="two">
              {REASONS.map((x) => (
                <button key={x} type="button" className="ghost" disabled={busy}
                        onClick={() => act('decline', { reason: x, note: note || null })}>{t(`why_${x}` as Key)}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      {msg && <div className="toast" role="status">{msg}</div>}
      {d.status === 'offered' && !declining && (
        <div className="two" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, gap: 0 }}>
          <button className="main" style={{ position: 'static', background: '#fff', color: '#111', borderTop: '1px solid #d9d8d3' }}
                  type="button" disabled={busy} onClick={() => setDeclining(true)}>{t('decline')}</button>
          <button className="main" style={{ position: 'static' }} type="button" disabled={busy} onClick={() => act('accept')}>{t('accept')}</button>
        </div>
      )}
    </div>
  );
}
